import React, { FC, useEffect, useState, memo } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useOnboardingContext, useOnboardingActionContext } from 'application/components/common';
import { SelectPlayerFlowType } from 'application/components/common/SelectPlayerFlow/types';
import { RouterUtils, useNavigate, useParams } from 'application/providers/RouterProvider';
import { StreamGroupsCollectionPageParamsType } from 'application/pages/types';
import { useNotificationContext } from 'application/providers/NotificationProvider';
import { POINTS_PAGE_PATH } from 'application/consts';
import { TEXTS } from 'application/pages/PointsPage/renderer/texts';

import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';

import usePointCreateViewModel from './usePointCreateModalViewModel';
import { PointCreateModalPages } from './types';
import { PopularTariffWarning, ModalTitle, ModalContent, PointCreateButton } from './components';

import styles from './index.module.scss';

const PointCreateModal: FC<ModalProps[ModalTypes.POINT_CREATE]> = ({
  autoOpened = false,
  close,
  replaceModal,
  openModal,
}) => {
  const { flowType, isTemplateMode } = useOnboardingContext();
  const { setCurrentPoint, setCurrentPointStream, setCurrentPointIsEnabled } = useOnboardingActionContext();
  const pathParams = useParams<StreamGroupsCollectionPageParamsType>();
  const navigate = useNavigate();
  const notification = useNotificationContext();
  const { isIpad } = useMedia();
  const [isAutocompleteFullMode, setIsAutocompleteFullMode] = useState(false);

  const targetStreamId = pathParams.id;
  const collectionId = pathParams.collectionId;
  const page =
    flowType === SelectPlayerFlowType.BIND_STREAM || flowType === SelectPlayerFlowType.CREATE_POINT_WHILE_BINDING_STREAM
      ? PointCreateModalPages.STREAM
      : PointCreateModalPages.POINT;

  const {
    addressState,
    isButtonLoading,
    createPointError,
    extendCost,
    needShowAcceptanceAdvancedModal,
    isAcceptanceAdvancedNull,
    isAcceptanceAdvancedPending,
    canAddPointWithPayment,
    isButtonDisabled,
    isShowHintAboutPrice,
    withResidualPayment,
    formatNextBillingDate,
    fromTime,
    toTime,
    onFromTimeChange,
    onToTimeChange,
    onFromTimeBlur,
    onToTimeBlur,
    onCreatePointButtonClick,
    openAcceptanceAdvancedSigning,
  } = usePointCreateViewModel({
    flowType,
    isTemplateMode,
    targetStreamId,
    collectionId,
    page,
    notification,
    setCurrentPoint,
    setCurrentPointStream,
    setCurrentPointIsEnabled,
    redirectToPointDrawer: (pointId: string) => {
      navigate(RouterUtils.generatePath(POINTS_PAGE_PATH, { id: pointId }), { replace: true });
    },
    replaceModal,
    openModal,
  });

  useEffect(() => {
    if (needShowAcceptanceAdvancedModal) {
      replaceModal(ModalTypes.ACCEPTANCE_ADVANCED, {});
    }
  }, [needShowAcceptanceAdvancedModal]);

  const getActionButtons = () => {
    const buttons = [];

    if (isAcceptanceAdvancedPending && !autoOpened) {
      buttons.push(
        <Button
          elementId="button_redirect_to_acceptance_advanced"
          key="button_redirect_to_acceptance_advanced"
          type={ButtonType.PRIMARY}
          onClick={() => {
            openAcceptanceAdvancedSigning();
            close();
          }}>
          {TEXTS.POINT_CREATE_ACCEPTANCE_ADVANCED_TEXT}
        </Button>
      );
    }

    buttons.push(
      <PointCreateButton
        key="button_create_new_point_modal"
        elementId="button_create_new_point_modal"
        isPointPage={page === PointCreateModalPages.POINT}
        onClick={onCreatePointButtonClick}
        isDisabled={isButtonDisabled}
        isLoading={isButtonLoading}
        isAcceptanceAdvancedPending={isAcceptanceAdvancedPending}
        canAddPointWithPayment={canAddPointWithPayment}
        extendCost={extendCost}
      />
    );

    return buttons;
  };

  return (
    <ModalScreen
      title={
        <ModalTitle
          isAcceptanceAdvancedNull={isAcceptanceAdvancedNull}
          isPointPage={page === PointCreateModalPages.POINT}
          canAddPointWithPayment={canAddPointWithPayment}
        />
      }
      titlePlacement="left"
      onClose={close}
      contentClassName={isIpad ? styles.content : undefined}
      hideCloseButton={isAutocompleteFullMode}
      actionButtons={getActionButtons()}>
      <PopularTariffWarning isStreamPage={page === PointCreateModalPages.STREAM} />

      <ModalContent
        isAcceptanceAdvancedNull={isAcceptanceAdvancedNull}
        isAcceptanceAdvancedPending={isAcceptanceAdvancedPending}
        addressState={addressState}
        createPointError={createPointError}
        isShowHintAboutPrice={isShowHintAboutPrice}
        withResidualPayment={withResidualPayment}
        amount={extendCost}
        period={formatNextBillingDate}
        fromTime={fromTime}
        toTime={toTime}
        onFromTimeChange={onFromTimeChange}
        onToTimeChange={onToTimeChange}
        onFromTimeBlur={onFromTimeBlur}
        onToTimeBlur={onToTimeBlur}
        onAutocompleteFullModeChange={setIsAutocompleteFullMode}
        openModal={openModal}
      />
    </ModalScreen>
  );
};

export default memo(PointCreateModal);
